import { type Content } from '@/features/cms/components/CMS/types'
import { deferredHydrationQueryOptions } from '@/features/cms/services/CMS/queries'
import { getCMSShelfLoader } from '@/features/cms/components/CMS/CMSLoaders'
import { useEffect, useState, type JSX } from 'react'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import {
  loadBeaconsFromCMSContent,
  trackCmsExperimentsInDatadog,
} from '@/features/cms/components/CMS/utils'
import { datadogRum } from '@datadog/browser-rum'
import { useQuery } from '@tanstack/react-query'

type Props = {
  data: Content
  children: (data: Content) => JSX.Element
  index: number
}

export const DeferredShelf = ({ data, children, index }: Props) => {
  const [isInView, setIsInView] = useState(false)
  const {
    isError,
    isLoading,
    data: deferredShelvesData,
  } = useQuery({
    ...deferredHydrationQueryOptions(data.hydration_callback),
    enabled: isInView,
  })

  useEffect(() => {
    if (!deferredShelvesData) return

    if (!deferredShelvesData.data) {
      // hydration callback was successful but there was no data
      datadogRum.addAction('deferredShelfPruned', {
        contentTypeId: deferredShelvesData.content_type_id,
        shelfIndex: index,
      })
      return
    }

    loadBeaconsFromCMSContent([deferredShelvesData])
    trackCmsExperimentsInDatadog([deferredShelvesData])
  }, [deferredShelvesData, index])

  if (!data.hydration_callback) {
    return children(data)
  }

  // show loading shelf when deferred call is loading OR we have not yet kicked off a deferred call
  if (isLoading || !isInView) {
    const loader = getCMSShelfLoader(data.content_type_id)
    return (
      <ScrollIntoView
        onInView={() => {
          setIsInView(true)
        }}
        // we want to kick off deferred fetch before the content is within view
        rootMargin="400px"
        threshold={0.1}
      >
        {loader}
      </ScrollIntoView>
    )
  }

  if (isError || !deferredShelvesData?.data) {
    return null
  }

  return children(deferredShelvesData)
}
